import {Identifier} from "../common/Identifier";
import {OptionType} from "../../components/form/SearchableSelect";
import {ProjectStagePhaseModule} from "./ProjectStagePhase";
import {IdAndValue} from "../common/IdAndValue";
import {Nullable} from "../common/Nullable";
import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {ProjectAreaName} from "./Project";
import {Nameable, Shorthandable} from "../common/Nameable";
import {Describable} from "../common/Describable";
import {SoftDeletable} from "../common/SoftDeletable";

/**
 * Module for managing phase templates.
 *
 * templates are company wide master data, not bound to a single project,
 * so permissions are checked against the general user group privileges only.
 * must match the backend module identifier of the backend module
 */
export const ProjectStagePhaseTemplateModule: Module = {
    identifier: "project_stage_phase_templates",
    isPrivileged: ( authUser, privilege ) =>
        isPrivileged(
            authUser,
            ProjectAreaName,
            ProjectStagePhaseTemplateModule.identifier,
            privilege
        )
};

export interface ProjectStagePhaseTemplateId {
    phaseTemplateId: Identifier<number>
}

export interface ProjectStagePhaseTplOptionType extends OptionType {
    shorthand: string
}


/**
 * A phase template is a predefined name ( and shorthand/description ) for project phases.
 *
 * during project creation a phase is created based on such a template, the template
 * name/shorthand/description are copied into the phase. existing phases are therefore
 * not affected by later template changes.
 *
 * in germany everything in construction must be done based on HOAI laws
 *
 */
export interface ProjectStagePhaseTemplate
    extends ProjectStagePhaseTemplateId, Nameable, Shorthandable, Describable, SoftDeletable {

}

export type ProjectStagePhaseTemplateProp = {
    phaseTemplate: ProjectStagePhaseTemplate
}

export interface ProjectPhaseTemplateIdProp {
    phaseTemplateId: number
}

/**
 * FETCH TEMPLATE SELECT OPTIONS API PATH
 *
 * Important:
 * baseWeb path must be included later
 */
export const PHASE_TEMPLATE_SELECT_API_ROUTE = `projects/stage/phase/templates/select`;

/**
 * ProjectPhaseTemplateTaskOptionType
 *
 * select option type to fetch phase template attached tasks.
 * these tasks also include attached task predefined descriptions
 *
 *  during project creation a user can add or remove task from a phase.
 * this grants a high level of control which tasks the users can actually select per phase.
 * false entries in the timesheet should be mitigated this way
 *
 * @property value the id of the timesheet task
 * @property label the title of the timesheet task
 * @property phaseTemplateId the phase template id the task is attached to
 * @property descriptions the description of the task, that are attached in the template
 */
export interface ProjectPhaseTemplateTaskOptionType extends OptionType, ProjectPhaseTemplateIdProp {
    descriptions: ProjectPhaseTemplateTaskDescriptionOptionType[]
    externalWork: Nullable< IdAndValue<number, string> >
}

export interface ProjectPhaseTemplateTaskDescriptionOptionType extends OptionType, ProjectPhaseTemplateIdProp {
    taskId: number
}

/**
 * identifier for select components, when handling ProjectPhaseTemplateTaskOptionType
 */
export const PHASE_TPL_TASK_IDENTIFIER = ProjectStagePhaseModule.identifier + '_tpl_tasks';

/**
 * api route to fetch select options for phase template tasks
 *
 * @see ProjectPhaseTemplateTaskOptionType
 *
 * @param phaseTemplateId
 * @param externalWorkGroupId
 */
export function phaseTemplateTasksSelectRoute( phaseTemplateId: number, externalWorkGroupId: Nullable< number > = null ): string {
    return `projects/stage/phase/templates/${ phaseTemplateId }/tasks/select/${( externalWorkGroupId === null ) ? 0 : externalWorkGroupId}`;
}